import { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';

// Ref: GitHub issue #36, task S7 — inactivity logout after 30 minutes
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

/**
 * Starts a countdown timer that calls `authStore.logout()` when the user has
 * been idle for `timeoutMs` milliseconds. The timer is reset on any of the
 * tracked interaction events. The hook is a no-op when the user is not
 * authenticated, and cleans up all listeners on unmount.
 */
export function useIdleTimer(timeoutMs: number = IDLE_TIMEOUT_MS): void {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const logout = useAuthStore((state) => state.logout);

  useEffect(() => {
    if (!isAuthenticated) return;

    let timer: ReturnType<typeof setTimeout>;

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        logout();
      }, timeoutMs);
    };

    const TRACKED_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'] as const;

    TRACKED_EVENTS.forEach((event) =>
      window.addEventListener(event, resetTimer, { passive: true }),
    );

    // Start the initial countdown
    resetTimer();

    return () => {
      clearTimeout(timer);
      TRACKED_EVENTS.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [isAuthenticated, logout, timeoutMs]);
}
